import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createRoom, updateRoom, getRoom } from '../../utils/api';
import { useApp } from '../../context/AppContext';
import DocumentsSection from '../documents/DocumentsSection';

const defaultForm = {
  name: '',
  floor: '1st',
  roomType: 'other',
  squareFootage: '',
  ceilingHeight: '',
  wallPaintColor: '',
  wallPaintBrand: '',
  wallPaintFinish: '',
  trimColor: '',
  trimPaintBrand: '',
  ceilingType: '',
  ceilingColor: '',
  flooringType: '',
  flooringMaterial: '',
  windowCount: '',
  windowNotes: '',
  notes: '',
  outlets: [],
  hvacVents: [],
  lightFixtures: [],
};

const blankOutlet = () => ({ location: '', breakerNumber: '', breakerLabel: '', amperage: '' });
const blankVent = () => ({ ventType: 'supply', location: '' });
const blankFixture = () => ({ fixtureType: '', location: '', switchLocation: '' });

const Section = ({ title, children }) => (
  <fieldset className="form-section">
    <legend className="form-section-title">{title}</legend>
    {children}
  </fieldset>
);

const RoomForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fetchRooms } = useApp();
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const isEditing = Boolean(id);

  useEffect(() => {
    if (!isEditing) return;
    getRoom(id)
      .then(({ data }) => {
        setForm({
          ...defaultForm,
          ...data,
          squareFootage: data.squareFootage ?? '',
          ceilingHeight: data.ceilingHeight ?? '',
          windowCount: data.windowCount ?? '',
          outlets: data.outlets ?? [],
          hvacVents: data.hvacVents ?? [],
          lightFixtures: data.lightFixtures ?? [],
        });
      })
      .catch(() => setLoadError('Room not found.'));
  }, [id, isEditing]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // Generic sub-array handlers
  const updateArrayItem = (field, index, key, value) => {
    setForm((prev) => {
      const updated = [...prev[field]];
      updated[index] = { ...updated[index], [key]: value };
      return { ...prev, [field]: updated };
    });
  };

  const addArrayItem = (field, blank) => {
    setForm((prev) => ({ ...prev, [field]: [...prev[field], blank()] }));
  };

  const removeArrayItem = (field, index) => {
    setForm((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEditing) {
        await updateRoom(id, form);
      } else {
        await createRoom(form);
      }
      await fetchRooms({ force: true });
      navigate('/rooms');
    } finally {
      setSaving(false);
    }
  };

  if (loadError) return <div className="page"><p className="empty-state">{loadError}</p></div>;

  return (
    <div className="page room-form-page">
      <div className="page-header">
        <h1>{isEditing ? 'Edit Room' : 'New Room'}</h1>
        <button className="btn btn-secondary" onClick={() => navigate('/rooms')}>Cancel</button>
      </div>

      <form onSubmit={handleSubmit} className="room-form">

        <Section title="Basic Info">
          <div className="form-row">
            <label>Room Name *
              <input name="name" value={form.name} onChange={handleChange} required />
            </label>
            <label>Floor *
              <select name="floor" value={form.floor} onChange={handleChange}>
                {['basement', '1st', '2nd', '3rd', 'attic'].map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
            </label>
            <label>Room Type
              <select name="roomType" value={form.roomType} onChange={handleChange}>
                {['bedroom', 'bathroom', 'kitchen', 'living room', 'dining room', 'office', 'garage', 'laundry', 'hallway', 'other'].map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </label>
          </div>
          <div className="form-row">
            <label>Square Footage
              <input type="number" name="squareFootage" value={form.squareFootage} onChange={handleChange} min={0} />
            </label>
            <label>Ceiling Height (ft)
              <input type="number" name="ceilingHeight" value={form.ceilingHeight} onChange={handleChange} min={0} step="0.5" />
            </label>
          </div>
        </Section>

        <Section title="Paint & Finishes">
          <div className="form-row">
            <label>Wall Paint Color
              <input name="wallPaintColor" value={form.wallPaintColor} onChange={handleChange} placeholder="e.g. Agreeable Gray SW 7029" />
            </label>
            <label>Wall Paint Brand
              <input name="wallPaintBrand" value={form.wallPaintBrand} onChange={handleChange} placeholder="e.g. Sherwin-Williams" />
            </label>
            <label>Wall Finish
              <select name="wallPaintFinish" value={form.wallPaintFinish} onChange={handleChange}>
                <option value="">— select —</option>
                {['flat', 'eggshell', 'satin', 'semi-gloss', 'gloss'].map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
            </label>
          </div>
          <div className="form-row">
            <label>Trim Color
              <input name="trimColor" value={form.trimColor} onChange={handleChange} />
            </label>
            <label>Trim Paint Brand
              <input name="trimPaintBrand" value={form.trimPaintBrand} onChange={handleChange} />
            </label>
          </div>
          <div className="form-row">
            <label>Ceiling Type
              <select name="ceilingType" value={form.ceilingType} onChange={handleChange}>
                <option value="">— select —</option>
                {['drywall', 'popcorn', 'coffered', 'vaulted', 'drop', 'wood', 'other'].map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </label>
            <label>Ceiling Color
              <input name="ceilingColor" value={form.ceilingColor} onChange={handleChange} />
            </label>
          </div>
        </Section>

        <Section title="Flooring">
          <div className="form-row">
            <label>Flooring Type
              <select name="flooringType" value={form.flooringType} onChange={handleChange}>
                <option value="">— select —</option>
                {['hardwood', 'laminate', 'carpet', 'tile', 'vinyl', 'concrete', 'other'].map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
            </label>
            <label>Material / Color / Brand
              <input name="flooringMaterial" value={form.flooringMaterial} onChange={handleChange} placeholder="e.g. Oak, Espresso stain" />
            </label>
          </div>
        </Section>

        <Section title="Electrical — Outlets">
          {form.outlets.map((outlet, i) => (
            <div key={i} className="sub-item">
              <div className="form-row">
                <label>Location
                  <input
                    value={outlet.location}
                    onChange={(e) => updateArrayItem('outlets', i, 'location', e.target.value)}
                    placeholder="e.g. North wall, left of door"
                  />
                </label>
                <label>Breaker #
                  <input
                    type="number"
                    value={outlet.breakerNumber}
                    onChange={(e) => updateArrayItem('outlets', i, 'breakerNumber', e.target.value)}
                  />
                </label>
                <label>Breaker Label
                  <input
                    value={outlet.breakerLabel}
                    onChange={(e) => updateArrayItem('outlets', i, 'breakerLabel', e.target.value)}
                    placeholder="e.g. Living Room"
                  />
                </label>
                <label>Amperage
                  <input
                    type="number"
                    value={outlet.amperage}
                    onChange={(e) => updateArrayItem('outlets', i, 'amperage', e.target.value)}
                    placeholder="15"
                  />
                </label>
              </div>
              <button type="button" className="btn btn-danger btn-sm" onClick={() => removeArrayItem('outlets', i)}>
                Remove
              </button>
            </div>
          ))}
          <button type="button" className="btn btn-secondary" onClick={() => addArrayItem('outlets', blankOutlet)}>
            + Add Outlet
          </button>
        </Section>

        <Section title="HVAC Vents">
          {form.hvacVents.map((vent, i) => (
            <div key={i} className="sub-item">
              <div className="form-row">
                <label>Type
                  <select
                    value={vent.ventType}
                    onChange={(e) => updateArrayItem('hvacVents', i, 'ventType', e.target.value)}
                  >
                    <option value="supply">Supply</option>
                    <option value="return">Return</option>
                  </select>
                </label>
                <label>Location
                  <input
                    value={vent.location}
                    onChange={(e) => updateArrayItem('hvacVents', i, 'location', e.target.value)}
                    placeholder="e.g. Floor, east wall"
                  />
                </label>
              </div>
              <button type="button" className="btn btn-danger btn-sm" onClick={() => removeArrayItem('hvacVents', i)}>
                Remove
              </button>
            </div>
          ))}
          <button type="button" className="btn btn-secondary" onClick={() => addArrayItem('hvacVents', blankVent)}>
            + Add Vent
          </button>
        </Section>

        <Section title="Light Fixtures">
          {form.lightFixtures.map((fixture, i) => (
            <div key={i} className="sub-item">
              <div className="form-row">
                <label>Fixture Type
                  <input
                    value={fixture.fixtureType}
                    onChange={(e) => updateArrayItem('lightFixtures', i, 'fixtureType', e.target.value)}
                    placeholder="e.g. Ceiling fan, recessed"
                  />
                </label>
                <label>Location
                  <input
                    value={fixture.location}
                    onChange={(e) => updateArrayItem('lightFixtures', i, 'location', e.target.value)}
                    placeholder="e.g. Center ceiling"
                  />
                </label>
                <label>Switch Location
                  <input
                    value={fixture.switchLocation}
                    onChange={(e) => updateArrayItem('lightFixtures', i, 'switchLocation', e.target.value)}
                    placeholder="e.g. Left of entry door"
                  />
                </label>
              </div>
              <button type="button" className="btn btn-danger btn-sm" onClick={() => removeArrayItem('lightFixtures', i)}>
                Remove
              </button>
            </div>
          ))}
          <button type="button" className="btn btn-secondary" onClick={() => addArrayItem('lightFixtures', blankFixture)}>
            + Add Fixture
          </button>
        </Section>

        <Section title="Windows">
          <div className="form-row">
            <label>Window Count
              <input type="number" name="windowCount" value={form.windowCount} onChange={handleChange} min={0} />
            </label>
            <label>Window Notes
              <input name="windowNotes" value={form.windowNotes} onChange={handleChange} placeholder="e.g. Double-pane, 2020 replacement" />
            </label>
          </div>
        </Section>

        <Section title="Notes">
          <label>
            <textarea name="notes" value={form.notes} onChange={handleChange} rows={3} placeholder="Anything else worth knowing about this room…" />
          </label>
        </Section>

        <div className="form-actions form-actions-bottom">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving…' : isEditing ? 'Save Changes' : 'Create Room'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/rooms')}>
            Cancel
          </button>
        </div>

      </form>

      {isEditing && <DocumentsSection roomId={id} />}
    </div>
  );
};

export default RoomForm;
